import { SIGNUP_LEGAL_DOCUMENT_PATHS } from './legal/legal-document-paths';
import { ALL_SIGNUP_STEPS, SIGNUP_ENTRY_PATH, type SignupStepId } from './steps';

/**
 * Signup pages that sit outside the main path: a member reaches them from a
 * failure, a link or a branch, never by Continue. Each is a static page under
 * the entry route and a registered screen. They are not in `SIGNUP_STEPS`, so
 * `nextSignupStep` can never land on one. A slice that adds another off-path
 * page appends its id here.
 */
export const SIGNUP_OFF_PATH_PAGES = [
  'error',
  'book-a-call',
  'login',
  'login-code',
  'login-no-progress',
] as const;

export type SignupOffPathPageId = (typeof SIGNUP_OFF_PATH_PAGES)[number];

/** Any page in the signup flow: a main-path step or an off-path page. */
export type SignupPageId = SignupStepId | SignupOffPathPageId;

/** Where every failure that production sends to its error screen goes. */
export const SIGNUP_ERROR_PAGE: SignupOffPathPageId = 'error';

/** The "Book a call" page, reached from the error screen and every footer. */
export const SIGNUP_BOOK_A_CALL_PAGE: SignupOffPathPageId = 'book-a-call';

/**
 * Every page sits under the entry route, so moving the flow root moves every
 * page with it. Static export uses trailing slashes, so every page path ends
 * in one.
 */
export function signupPagePath(page: SignupPageId): string {
  return `${SIGNUP_ENTRY_PATH}${page}/`;
}

/** Returning-member login (`?login`): the phone and email tabs. */
export const SIGNUP_LOGIN_PAGE: SignupOffPathPageId = 'login';

/** The one-time code a returning member signs in with. */
export const SIGNUP_LOGIN_CODE_PAGE: SignupOffPathPageId = 'login-code';

/** A signed-in returning member with nothing to resume from. */
export const SIGNUP_LOGIN_NO_PROGRESS_PAGE: SignupOffPathPageId = 'login-no-progress';

const SIGNUP_PAGE_PATHS: ReadonlySet<string> = new Set([
  SIGNUP_ENTRY_PATH,
  ...[...ALL_SIGNUP_STEPS, ...SIGNUP_OFF_PATH_PAGES].map(signupPagePath),
  ...Object.values(SIGNUP_LEGAL_DOCUMENT_PATHS),
]);

/**
 * Whether a browser path is one of the signup flow's own pages (with or
 * without the trailing slash). Built from the page lists rather than a path
 * prefix, so moving the flow root keeps it right.
 */
export function isSignupPagePathname(pathname: string): boolean {
  return SIGNUP_PAGE_PATHS.has(pathname.endsWith('/') ? pathname : `${pathname}/`);
}
